import { rellenarPosicionamiento } from "./pdf"
import original from "./solicitud-posicionamiento.pdf?url"
import type { DatosPosicionamiento } from "./tipos"

/**
 * Sacar por la impresora el PDF de una solicitud.
 *
 * ⚠ **Se imprime el PDF, NO la pantalla.** Es la única forma de que lo guardado en el
 * registro sea exactamente lo que salió: el navegador no deja capturar lo que manda a
 * la impresora, así que el papel tiene que salir de un archivo que ya tenemos. Ver
 * `pdf.ts`.
 *
 * ⚠ **La página original viene de un `import ?url` y no de `public/`.** Vite la emite
 * en `/assets/` con un hash en el nombre: no puede chocar con la ruta `/plantillas`
 * —el 403 que ya dio el logotipo— y el navegador no se queda con la vieja si la aduana
 * publica otra.
 */

let pagina: Promise<ArrayBuffer> | null = null

/** La página del SENIAT, pedida una sola vez por sesión. */
function paginaOriginal(): Promise<ArrayBuffer> {
  if (pagina === null) {
    const pedida = fetch(original).then((respuesta) => {
      if (!respuesta.ok) {
        throw new Error(`No se pudo cargar el formato original (estado ${respuesta.status}).`)
      }
      return respuesta.arrayBuffer()
    })
    // Un fallo no se queda guardado: el siguiente «Imprimir» la vuelve a pedir.
    pedida.catch(() => {
      pagina = null
    })
    pagina = pedida
  }
  return pagina
}

/** El PDF de la solicitud: la página original con lo tecleado encima. */
export async function pdfDePosicionamiento(
  datos: DatosPosicionamiento,
): Promise<Uint8Array<ArrayBuffer>> {
  return rellenarPosicionamiento(await paginaOriginal(), datos)
}

let marco: HTMLIFrameElement | null = null
let enlace: string | null = null

/** Retira el marco de la última impresión y suelta su archivo en memoria. */
export function soltarImpresion(): void {
  marco?.remove()
  marco = null
  if (enlace !== null) URL.revokeObjectURL(enlace)
  enlace = null
}

/**
 * Abre el diálogo de impresión con el PDF dentro.
 *
 * ⚠ **El marco se oculta con `visibility`, no con `display: none`.** Un marco sin
 * maquetar no carga el visor de PDF del navegador, y la hoja saldría en blanco.
 *
 * ⚠ **El marco se queda hasta la siguiente impresión.** El diálogo sigue leyendo el
 * archivo mientras está abierto; soltarlo al volver de `print()` lo dejaría sin nada
 * que imprimir en los navegadores donde esa llamada no bloquea.
 */
export function imprimirPdf(pdf: Uint8Array<ArrayBuffer>): void {
  soltarImpresion()
  const url = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }))
  const nuevo = document.createElement("iframe")
  nuevo.title = "Solicitud para imprimir"
  nuevo.setAttribute("aria-hidden", "true")
  nuevo.tabIndex = -1
  nuevo.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden"
  nuevo.addEventListener(
    "load",
    () => {
      try {
        nuevo.contentWindow?.focus()
        nuevo.contentWindow?.print()
      } catch {
        abrirPdf(pdf)
      }
    },
    { once: true },
  )
  nuevo.src = url
  document.body.appendChild(nuevo)
  marco = nuevo
  enlace = url
}

/**
 * El mismo PDF en una pestaña, para quien no ve el diálogo.
 *
 * Es la salida en los navegadores que no imprimen un PDF desde un marco —los de los
 * teléfonos, sobre todo—: el registro ya está guardado, y desde el visor se imprime.
 */
export function abrirPdf(pdf: Uint8Array<ArrayBuffer>): void {
  const url = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }))
  window.open(url, "_blank")
  // Se suelta cuando la pestaña ya lo cargó; hacerlo al instante la dejaría en blanco.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
